import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchShippedShortfallWithStock,
  type ShippedShortfallWithStockResponse,
} from '../api/reports';
import { borderRadius, colors, spacing, typography, typographyFlat } from '../styles/tokens';
import { formatInstant } from '../utils/datetime';

export const ReportsShippedShortfallPage: React.FC = () => {
  const [report, setReport] = useState<ShippedShortfallWithStockResponse | null>(null);
  const [source, setSource] = useState('');
  const [limit, setLimit] = useState('100');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildParams = useCallback(() => {
    const parsedLimit = Number.parseInt(limit, 10);
    return {
      limit: Number.isNaN(parsedLimit) || parsedLimit <= 0 ? 100 : Math.min(parsedLimit, 5000),
      source: source.trim() || undefined,
    };
  }, [limit, source]);

  const loadReport = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const nextReport = await fetchShippedShortfallWithStock(buildParams());
      setReport(nextReport);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shipped shortfall report');
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    void loadReport();
  }, []);

  const handleApplyFilters = async (event: React.FormEvent) => {
    event.preventDefault();
    await loadReport();
  };

  const summary = report?.summary;
  const isCritical = (report?.count ?? 0) > 0;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Reports: Shipped Shortfall w/ Stock</h1>
          <p style={styles.subtitle}>
            Shipped channel orders with open shortfall that still have matching SyncBase sellable
            stock. These are ghost re-list risks while shipping stays external — treat shortfall as
            shrink; do not remediae quantity on these variants.
          </p>
        </div>
        <div style={styles.headerLinks}>
          <Link to="/reports/channel-parity" style={styles.linkButton}>Channel Parity</Link>
          <Link to="/dashboards" style={styles.linkButton}>Channel Operations</Link>
        </div>
      </div>

      <form style={styles.filtersRow} onSubmit={handleApplyFilters}>
        <label style={styles.filterLabel}>
          Source
          <input
            value={source}
            onChange={(event) => setSource(event.target.value)}
            style={styles.input}
            placeholder="manapool"
          />
        </label>
        <label style={styles.filterLabel}>
          Limit
          <input
            type="number"
            min={1}
            max={5000}
            value={limit}
            onChange={(event) => setLimit(event.target.value)}
            style={styles.input}
          />
        </label>
        <div style={styles.actions}>
          <button type="submit" style={styles.primaryButton} disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </form>

      {error && <div style={styles.error}>{error}</div>}

      {report && (
        <>
          <div style={{
            ...styles.alertBanner,
            borderColor: isCritical ? colors.error : colors.success,
            backgroundColor: isCritical ? colors.errorLight : `${colors.success}15`,
          }}>
            <strong>
              {isCritical
                ? `Critical: ${report.count} line(s) with shipped shortfall + remaining stock`
                : 'Clear: no shipped shortfall with remaining SyncBase stock'}
            </strong>
            <span style={styles.metaText}>
              Generated {formatInstant(report.generated_at, '-')}
              {report.source_filter ? ` · source ${report.source_filter}` : ''}
            </span>
          </div>

          <div style={styles.summaryGrid}>
            <MetricCard
              label="Lines"
              value={String(summary?.line_count ?? report.count)}
              detail="Open shortfall + stock"
              tone={isCritical ? 'error' : 'default'}
            />
            <MetricCard
              label="Shortfall qty"
              value={String(summary?.total_shortfall_qty ?? 0)}
              detail="Cards never FIFO-deducted"
              tone={isCritical ? 'error' : 'default'}
            />
            <MetricCard
              label="Still in stock"
              value={String(summary?.total_in_stock_now ?? 0)}
              detail="Matching sellable copies"
              tone={isCritical ? 'error' : 'default'}
            />
            <MetricCard
              label="Orders / variants"
              value={`${summary?.unique_orders ?? 0} / ${summary?.unique_variants ?? 0}`}
              detail="Distinct orders · variants"
            />
          </div>

          <section style={styles.card}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Highest risk first</h2>
              <span style={styles.metaText}>
                {report.rows.length < report.count
                  ? `Showing ${report.rows.length} of ${report.count} lines`
                  : `${report.rows.length} line(s)`}
                {' · Sorted by in-stock qty, then shortfall, then newest order'}
              </span>
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Card</th>
                    <th style={styles.th}>Set / #</th>
                    <th style={styles.th}>Finish</th>
                    <th style={styles.th}>Source</th>
                    <th style={styles.th}>Order</th>
                    <th style={styles.th}>Shortfall</th>
                    <th style={styles.th}>In stock</th>
                    <th style={styles.th}>Variant</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.length === 0 && (
                    <tr>
                      <td colSpan={8} style={styles.emptyCell}>
                        No shipped shortfall lines with remaining stock.
                      </td>
                    </tr>
                  )}
                  {report.rows.map((row, index) => (
                    <tr key={`${row.order_external_id}-${row.variant_id}-${index}`}>
                      <td style={styles.tdStrong}>{row.card_name || '-'}</td>
                      <td style={styles.td}>
                        {[row.set_name, row.card_number].filter(Boolean).join(' #') || '-'}
                      </td>
                      <td style={styles.td}>{row.finish || '-'}</td>
                      <td style={styles.td}>{row.source || '-'}</td>
                      <td style={styles.tdMono}>{row.order_external_id || '-'}</td>
                      <td style={styles.tdStrong}>{row.shortfall_qty ?? '-'}</td>
                      <td style={styles.tdStrong}>{row.in_stock_now ?? '-'}</td>
                      <td style={styles.tdMono}>{row.variant_id || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

function MetricCard({
  label,
  value,
  detail,
  tone = 'default',
}: {
  label: string;
  value: string;
  detail: string;
  tone?: 'default' | 'error';
}) {
  return (
    <div style={styles.metricCard}>
      <span style={styles.metricLabel}>{label}</span>
      <strong style={{
        ...styles.metricValue,
        color: tone === 'error' ? colors.error : colors.text,
      }}
      >
        {value}
      </strong>
      <span style={styles.metricDetail}>{detail}</span>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: spacing.lg,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.md,
    flexWrap: 'wrap',
    marginBottom: spacing.lg,
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  subtitle: {
    marginTop: spacing.xs,
    marginBottom: 0,
    maxWidth: '720px',
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  headerLinks: {
    display: 'flex',
    gap: spacing.sm,
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  linkButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    color: colors.text,
    padding: `${spacing.sm} ${spacing.md}`,
    textDecoration: 'none',
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'end',
    marginBottom: spacing.md,
    padding: spacing.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
  },
  filterLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    color: colors.text,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    minWidth: '160px',
  },
  input: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typography.body.fontSize,
    color: colors.text,
    backgroundColor: colors.background,
  },
  actions: {
    display: 'flex',
    gap: spacing.sm,
  },
  primaryButton: {
    border: 'none',
    borderRadius: borderRadius.sm,
    backgroundColor: colors.primary,
    color: colors.white,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  error: {
    marginBottom: spacing.md,
    padding: spacing.md,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.error}`,
    backgroundColor: `${colors.error}20`,
    color: colors.error,
    fontSize: typography.body.fontSize,
  },
  alertBanner: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    marginBottom: spacing.md,
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border}`,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  metricCard: {
    padding: spacing.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  metricLabel: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  metricValue: {
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
  },
  metricDetail: {
    fontSize: typography.caption.fontSize,
    color: colors.textSecondary,
  },
  card: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
    padding: spacing.md,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.sm,
    flexWrap: 'wrap',
    marginBottom: spacing.md,
  },
  sectionTitle: {
    margin: 0,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
  },
  metaText: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '920px',
  },
  th: {
    textAlign: 'left',
    padding: spacing.sm,
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    fontWeight: typographyFlat.fontWeight.semibold,
    borderBottom: `1px solid ${colors.border}`,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: spacing.sm,
    fontSize: typography.body.fontSize,
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
  },
  tdStrong: {
    padding: spacing.sm,
    fontSize: typography.body.fontSize,
    color: colors.text,
    fontWeight: typographyFlat.fontWeight.semibold,
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
  },
  tdMono: {
    padding: spacing.sm,
    fontSize: typography.caption.fontSize,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    color: colors.textSecondary,
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
  },
  emptyCell: {
    padding: spacing.lg,
    textAlign: 'center',
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
};
